/**
 * Typed client for /escalations/* — backs the Escalations screen.
 *
 * Escalation = a human-review item produced by any of the agentic
 * pipeline stages (prospecting, outreach, registration, integration,
 * customer_service, inference_signup). The admin queue is where ops
 * triage them: assign to a teammate, resolve with notes, or dismiss.
 *
 * The list endpoint uses mongoose-paginate-v2, so the response shape
 * is `{ docs, totalDocs, limit, page, totalPages, ... }`. We type that
 * directly rather than transforming server-side.
 */
import { client } from './client';

export type SourceAgent =
  | 'prospecting'
  | 'outreach'
  | 'registration'
  | 'integration'
  | 'customer_service'
  | 'inference_signup';

export type EscalationStatus = 'open' | 'assigned' | 'resolved' | 'dismissed';
export type EscalationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface ProspectRef {
  _id: string;
  domain?: string;
  companyName?: string;
  contactName?: string;
  contactEmail?: string;
  status?: string;
  score?: number;
  monthlyTrafficTier?: string;
}

export interface PublisherRef {
  _id: string;
  name?: string;
  email?: string;
  companyName?: string;
}

export interface Escalation {
  _id: string;
  sourceAgent: SourceAgent;
  priority: EscalationPriority;
  prospectId?: ProspectRef | string | null;
  publisherId?: PublisherRef | string | null;
  reason: string;
  context?: Record<string, unknown>;
  status: EscalationStatus;
  assignedTo?: string | null;
  resolvedAt?: string | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EscalationListResponse {
  docs: Escalation[];
  totalDocs: number;
  limit: number;
  page: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
}

export interface EscalationStatsResponse {
  byStatus: Partial<Record<EscalationStatus, number>>;
  byPriority: Partial<Record<EscalationPriority, number>>;
  bySource: Partial<Record<SourceAgent, number>>;
  last24h: number;
}

export interface EscalationListParams {
  page?: number;
  limit?: number;
  status?: EscalationStatus | '';
  priority?: EscalationPriority | '';
  sourceAgent?: SourceAgent | '';
  sort?: string;
}

export interface EscalationUpdateInput {
  id: string;
  status?: EscalationStatus;
  assignedTo?: string;
  notes?: string;
  priority?: EscalationPriority;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchEscalations(
  params: EscalationListParams
): Promise<EscalationListResponse> {
  // Strip empty-string filter values so they don't end up in the query
  // string as `status=` (which the controller does filter on as truthy
  // — empty string is falsy in JS, but we send a clean URL anyway).
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  const r = await client.get<EscalationListResponse>('/escalations', {
    params: cleanParams
  });
  return r.data;
}

export async function fetchEscalationStats(): Promise<EscalationStatsResponse> {
  const r = await client.get<EscalationStatsResponse>('/escalations/stats');
  return r.data;
}

export async function updateEscalation(
  input: EscalationUpdateInput
): Promise<Escalation> {
  const { id, ...body } = input;
  const r = await client.patch<Escalation>(`/escalations/${id}`, body);
  return r.data;
}
